/**
 * Streams (campaigns) and routes, said in words.
 *
 * What a stream does is worked out by the server from its routing, the same
 * tables and switches the engine reads (`routes/v1/streams.rs`): sells,
 * assigns, sends, or stores only. Nothing here decides it; this only says it.
 *
 * Money is shown for a stream that has a selling route, on or paused
 * (`has_selling_routes`), so pausing the only buyer does not hide the price
 * needed to switch it back on.
 */
import { useCallback } from 'react';
import { Badge, Group, Tooltip, type MantineSpacing } from '@mantine/core';
import type { components } from '../../api/schema';
import { useCampaigns, useEnums } from '../../api/queries';
import type { Vocab } from '../../vocab';
import { isScopedUser } from '../../layout/nav';
import { useNavUser } from '../../layout/navUser';

export type StreamDoes = components['schemas']['StreamDoes'];
export type RouteKind = 'sell' | 'assign' | 'send';

/** Whether this stream's pages show prices, caps, supplier cost and returns. */
export const showsMoney = (does: StreamDoes | null | undefined) => does?.has_selling_routes !== false;

/**
 * Before the served list arrives: the default type. A row type is a label
 * the server serves (`row_types`), not a word from the term map, so it does
 * not go through it.
 */
// eslint-disable-next-line no-restricted-syntax -- a row type's served label, not the lead noun
const DEFAULT_ROW_TYPE = { value: 'lead', label: 'Lead' };

/** The served row types, value and label. */
export function useRowTypes() {
  const served = useEnums().data?.row_types ?? [];
  const rows = served.length ? served : [DEFAULT_ROW_TYPE];
  const label = (value: string | null | undefined) =>
    rows.find((r) => r.value === (value || 'lead'))?.label ?? value ?? DEFAULT_ROW_TYPE.label;
  return { options: rows.map((r) => ({ value: r.value, label: r.label })), label };
}

/**
 * Row types whose rows are not about a person, so checks on an email or a
 * phone (duplicates, suppression, HLR) start off on a new stream of one.
 * The server's `ROW_TYPES_NOT_ABOUT_A_PERSON` (routes/meta.rs) decides;
 * this copy only words the form before it is saved.
 */
const NOT_ABOUT_A_PERSON = new Set(['order', 'booking', 'other']);

export const rowTypeIsAboutAPerson = (rowType: string | null | undefined) =>
  !NOT_ABOUT_A_PERSON.has(rowType || 'lead');

/** A row's stream, as far as lists and headers need it. */
export type StreamOfRow = {
  /** The served row type value (`lead`, `order`...), or `reference` for a reference list. */
  rowType: string;
  /** In a sentence: "lead", "quote request", "reference list". */
  label: string;
  /** What the stream does; absent from a server older than this build. */
  does: StreamDoes | null;
};

/**
 * Whether a row's type is worth saying: whenever there is one, lead
 * included, since every row is a row and its type says what it is.
 */
export const showsRowType = (rowType: string | null | undefined) => !!rowType;

/**
 * Look up a row's stream by campaign id, from the streams list every
 * filter already loads. `null` while it loads, for an id it does not know,
 * and always for a buyer, supplier or other scoped login, whose server is
 * never asked for the streams list.
 */
export function useStreamOf() {
  const scoped = isScopedUser(useNavUser());
  const campaigns = useCampaigns(!scoped).data?.campaigns;
  const types = useRowTypes();
  const typeLabel = types.label;
  return useCallback((campaignId: string | null | undefined): StreamOfRow | null => {
    const c = campaignId ? campaigns?.find((x) => x.id === campaignId) : undefined;
    if (!c) return null;
    if (c.is_reference_list) return { rowType: 'reference', label: 'reference list', does: c.does ?? null };
    const rowType = c.row_type || 'lead';
    return { rowType, label: typeLabel(rowType).toLowerCase(), does: c.does ?? null };
  }, [campaigns, typeLabel]);
}

/** The row type as a small chip beside a stream's name, when it is worth saying. */
export function RowTypeChip({ stream, ml }: { stream: StreamOfRow | null; ml?: MantineSpacing | number }) {
  if (!stream || !showsRowType(stream.rowType)) return null;
  return (
    <Badge
      size="xs"
      variant="outline"
      color="gray"
      radius="sm"
      ml={ml}
      tt="none"
      fw={500}
      style={{ verticalAlign: 'text-bottom' }}
      data-testid="row-type-chip"
    >
      {stream.label}
    </Badge>
  );
}

type Chip = { key: string; label: string; colour: string; hint: string };

/** The "what it does" chips, in order: sells, assigns, sends, or stores only. */
export function doesChips(does: StreamDoes, v: Vocab): Chip[] {
  const out: Chip[] = [];
  if (does.sells) {
    out.push({
      key: 'sells',
      label: does.buyers > 0 ? `Sells to ${v.count(does.buyers, 'buyer')}` : 'Sells',
      colour: 'chartBlue',
      hint: `Has a live ${v.one('route')} to ${v.a('buyer')}: prices, caps and returns apply.`,
    });
  }
  if (does.assigns) {
    out.push({
      key: 'assigns',
      label: 'Assigns to reps',
      colour: 'chartGreen',
      hint: `A rotation gives ${v.many('lead')} nobody bought to the next rep on a team.`,
    });
  }
  if (does.sends) {
    out.push({
      key: 'sends',
      label: 'Sends to a webhook',
      colour: 'gray',
      hint: `Posts ${v.many('lead')} to an address with no ${v.one('buyer')} behind it.`,
    });
  }
  if (does.stores_only) {
    out.push({
      key: 'stores',
      label: 'Stores only',
      colour: 'gray',
      hint: `No live ${v.many('route')}: ${v.many('lead')} are kept and nothing else happens.`,
    });
    if (does.has_selling_routes) {
      out.push({
        key: 'paused',
        label: 'Selling paused',
        colour: 'chartAmber',
        hint: `Every ${v.one('route')} to ${v.a('buyer')} is switched off.`,
      });
    }
  }
  return out;
}

/**
 * The chips, or nothing when the server did not say (one older than this
 * build, for the minutes between a web deploy and a server one).
 */
export function DoesChips({
  does, v, size = 'sm',
}: { does: StreamDoes | null | undefined; v: Vocab; size?: 'xs' | 'sm' | 'md' }) {
  if (!does) return null;
  return (
    <Group gap={6} wrap="wrap" data-testid="does-chips">
      {doesChips(does, v).map((c) => (
        <Tooltip key={c.key} label={c.hint} multiline maw={280}>
          <Badge size={size} variant="light" color={c.colour} radius="xl" style={{ textTransform: 'none' }}>
            {c.label}
          </Badge>
        </Tooltip>
      ))}
    </Group>
  );
}

/** One sentence for the stream page: what happens to what arrives. */
export function doesSentence(does: StreamDoes, v: Vocab): string {
  const parts: string[] = [];
  if (does.sells) {
    parts.push(does.buyers > 0 ? `sold to ${v.count(does.buyers, 'buyer')}` : 'sold');
  }
  if (does.assigns) parts.push(does.sells ? 'given to a rep when nobody buys them' : 'given to a rep');
  if (does.sends) parts.push('posted to a webhook');
  if (parts.length === 0) {
    return does.has_selling_routes
      ? `${v.Many('lead')} are kept. Every ${v.one('route')} to ${v.a('buyer')} is switched off, so nothing is sold until one is back on.`
      : `${v.Many('lead')} are kept. Nothing else happens until this ${v.one('campaign')} has ${v.a('route')}.`;
  }
  const list = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  return `${v.Many('lead')} that arrive are ${list}.`;
}

/** A route's kind, in a word. */
export function kindLabel(kind: string): string {
  switch (kind) {
    case 'sell': return 'Sell';
    case 'assign': return 'Assign';
    case 'send': return 'Send';
    default: return kind;
  }
}

export const KIND_COLOUR: Record<string, string> = { sell: 'chartBlue', assign: 'chartGreen', send: 'gray' };
